// Server-sent events read off a fetch response body.
//
// The Ask Optivo assistant answers a POST with `text/event-stream`, which
// `EventSource` cannot read (it only GETs), so the body is read here: one
// event per blank-line-terminated block, `event:` naming it and every `data:`
// line joined. Comment lines (`: open`, `: ping`) keep the proxies from
// timing the connection out and carry nothing, so they are skipped — but each
// read that brought bytes is said through `onBytes`, which is what an idle
// watchdog listens to.

export interface ServerSentEvent {
    event: string;
    data: string;
}

/**
 * Reads a `text/event-stream` body (LF or CRLF line ends). `event` defaults to
 * "message", several `data:` lines join with "\n", and a block the body ended
 * in the middle of — no blank line after it — is dropped.
 *
 * Read through `getReader()` rather than `for await` over the body, which
 * Safari cannot iterate. An aborted `signal` cancels the reader, so a body a
 * server holds open without a word ends the reading rather than hanging it.
 */
export async function* readServerSentEvents(
    body: ReadableStream<Uint8Array>,
    onBytes?: () => void,
    signal?: AbortSignal,
): AsyncGenerator<ServerSentEvent> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const cancel = () => {
        reader.cancel().catch(() => {
            // Cancelling a body that already failed has nothing left to say.
        });
    };
    if (signal?.aborted) cancel();
    else signal?.addEventListener("abort", cancel, { once: true });
    let pending = "";
    let event = "message";
    let data: string[] = [];
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (signal?.aborted) return;
            if (!done) onBytes?.();
            pending += done ? decoder.decode() : decoder.decode(value, { stream: true });
            let newline: number;
            while ((newline = pending.indexOf("\n")) !== -1) {
                let line = pending.slice(0, newline);
                pending = pending.slice(newline + 1);
                if (line.endsWith("\r")) line = line.slice(0, -1);
                if (line === "") {
                    if (data.length > 0) yield { event, data: data.join("\n") };
                    if (signal?.aborted) return;
                    event = "message";
                    data = [];
                    continue;
                }
                if (line.startsWith(":")) continue;
                const colon = line.indexOf(":");
                const field = colon === -1 ? line : line.slice(0, colon);
                let fieldValue = colon === -1 ? "" : line.slice(colon + 1);
                if (fieldValue.startsWith(" ")) fieldValue = fieldValue.slice(1);
                if (field === "event") event = fieldValue;
                else if (field === "data") data.push(fieldValue);
            }
            if (done) return;
        }
    } finally {
        signal?.removeEventListener("abort", cancel);
        reader.releaseLock();
    }
}
